import { useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import {
  ArrowRight,
  Box,
  CreditCard,
  Download,
  FolderOpen,
  GripHorizontal,
  Info,
  Menu,
  Printer,
  RefreshCw,
  Save,
  SeparatorHorizontal,
  SeparatorVertical,
  Trash2,
  Upload,
  Utensils,
  X
} from "lucide-react";
import { NumberField } from "./components/NumberField";
import {
  clamp,
  designLimits,
  getTemplateDesign,
  hashDesign,
  normalizeDesign,
  type DividerAxis,
  type DividerSegment,
  type HandlePlacement,
  type StorageDesign,
  type TemplateId,
  type WallFace
} from "./domain/design";
import { parseStorageDesignJson } from "./domain/designImport";
import { parseSignedEntitlementJson, type SignedEntitlement } from "./domain/entitlement";
import { validateReexport } from "./domain/reexport";
import { createCheckoutSession, verifyCheckoutSession, verifyEntitlement } from "./api/checkout";
import { hakofitDesignType } from "./domain/pricing";
import { downloadProjectZip } from "./export/download";
import { staticPagePathMap, type LegalPageContent } from "./legalContent";
import { copyByLanguage, localizedLegalPages, type AppCopy, type Language, type PageSlug } from "./localization";
import { getExportUpgradeStatus, isNativeIosApp, purchaseExportUpgrade, restoreExportUpgrade } from "./native/exportUpgrade";
import { TrayScene } from "./preview/TrayScene";

type AppView = "landing" | "categories" | "editor";

type Notice = {
  kind: "info" | "error" | "success";
  message: string;
};

type EditableNumberField =
  | "widthMm"
  | "depthMm"
  | "heightMm"
  | "wallMm"
  | "bottomMm"
  | "cornerRadiusMm"
  | "handleLengthMm"
  | "dividerMm"
  | "dividerHeightMm";

type NumberFieldLimits = {
  max: number;
  min: number;
  step: number;
  unit?: string;
};

type NumberFieldErrors = Partial<Record<EditableNumberField, string>>;

type CheckoutStatus = "idle" | "creating" | "verifying" | "reexporting" | "purchasing" | "restoring" | "exporting";

type PendingCheckout = {
  createdAt: string;
  design: StorageDesign;
  designHash: string;
};

type SavedDesignRecord = {
  id: string;
  name: string;
  updatedAt: string;
  design: StorageDesign;
};

type CategoryMeta = {
  id: TemplateId;
  icon: typeof Box;
};

type EditorTool = "handle" | "divider-column" | "divider-row";
type CameraViewMode = "top" | "handle" | null;

type Selection =
  | { type: "handle"; id: string }
  | { type: "divider"; id: string };

type PointRatio = {
  face?: WallFace;
  vertical?: number;
  x: number;
  y: number;
};

type SeoMetadata = {
  title: string;
  description: string;
  path: string;
};

const pendingCheckoutStorageKey = "hakofit:pending-checkout";
const savedDesignsStorageKey = "hakofit:saved-designs";
const savedDesignLimit = 5;
const contactEmail = "contact@hakofit.com";
const siteOrigin = "https://hakofit.com";
const pagePathMap: Record<PageSlug, string> = {
  ...staticPagePathMap,
  contact: "/contact"
};

const categories: CategoryMeta[] = [
  {
    id: "basic-box",
    icon: Box
  },
  {
    id: "tcg-card-box",
    icon: CreditCard
  },
  {
    id: "cutlery-tray",
    icon: Utensils
  }
];

function App() {
  const isNativeIos = isNativeIosApp();
  const rootView: AppView = isNativeIos ? "categories" : "landing";
  const [view, setView] = useState<AppView>(rootView);
  const [language, setLanguage] = useState<Language>(() => getInitialLanguage());
  const [staticPageSlug, setStaticPageSlug] = useState<PageSlug | null>(() => getStaticPageSlugFromPath(window.location.pathname));
  const [design, setDesign] = useState<StorageDesign>(() => getTemplateDesign("basic-box"));
  const [savedDesigns, setSavedDesigns] = useState<SavedDesignRecord[]>(() => isNativeIos ? loadSavedDesigns() : []);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatus>("idle");
  const [exportUpgradePurchased, setExportUpgradePurchased] = useState(!isNativeIos);
  const checkoutHandledRef = useRef(false);
  const copy = useMemo(() => getRuntimeCopy(copyByLanguage[language], language, isNativeIos), [isNativeIos, language]);
  const normalizedDesign = useMemo(() => normalizeDesign(design), [design]);
  const numberFieldErrors = useMemo(() => getNumberFieldErrors(design, copy), [copy, design]);
  const hasNumberFieldErrors = hasFieldErrors(numberFieldErrors);

  useEffect(() => {
    applyDocumentSeo(getSeoMetadata(staticPageSlug, language), language);
  }, [language, staticPageSlug]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [view]);

  useEffect(() => {
    if (!notice) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => setNotice(null),
      notice.kind === "error" ? 6000 : 3200
    );

    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (!isNativeIos) {
      return;
    }

    let isMounted = true;

    getExportUpgradeStatus()
      .then((result) => {
        if (isMounted) {
          setExportUpgradePurchased(result.purchased);
        }
      })
      .catch(() => {
        if (isMounted) {
          setExportUpgradePurchased(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isNativeIos]);

  const updateDesign = (patch: Partial<StorageDesign>) => {
    setDesign((current) => ({ ...current, ...patch }));
  };

  const changeLanguage = (nextLanguage: Language) => {
    setLanguage(nextLanguage);
    saveLanguagePreference(nextLanguage);
  };

  const chooseTemplate = (templateId: TemplateId, nextView: AppView = "editor") => {
    setDesign(getTemplateDesign(templateId));
    setView(nextView);
    setNotice({ kind: "info", message: copy.notices.templateLoaded(copy.categories.items[templateId].title) });
  };

  const navigateHome = () => {
    window.history.pushState({}, "", "/");
    setStaticPageSlug(null);
    setView(rootView);
    setNotice(null);
    window.scrollTo({ top: 0, left: 0 });
  };

  const openAbout = () => {
    window.history.pushState({}, "", "/");
    setStaticPageSlug(null);
    setView("landing");
    setNotice(null);
    window.scrollTo({ top: 0, left: 0 });
  };

  const navigateStaticPage = (slug: PageSlug) => {
    window.history.pushState({}, "", pagePathMap[slug]);
    setStaticPageSlug(slug);
    setNotice(null);
    window.scrollTo({ top: 0, left: 0 });
  };

  const exportDesign = async () => {
    if (checkoutStatus !== "idle") {
      return;
    }

    if (hasNumberFieldErrors) {
      setNotice({ kind: "error", message: copy.editor.validation.fixBeforeDownload });
      return;
    }

    if (isNativeIos) {
      await exportDesignFromNativeIosUpgrade();
      return;
    }

    setCheckoutStatus("creating");

    try {
      const designHash = await hashDesign(normalizedDesign);

      savePendingCheckout({
        createdAt: new Date().toISOString(),
        design: normalizedDesign,
        designHash
      }, copy.notices.saveDesignFailed);

      const session = await createCheckoutSession(designHash);

      if (!session.checkoutUrl) {
        throw new Error(copy.notices.checkoutUrlMissing);
      }

      window.location.assign(session.checkoutUrl);
    } catch (error) {
      setCheckoutStatus("idle");
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    }
  };

  const exportDesignFromNativeIosUpgrade = async () => {
    try {
      if (!exportUpgradePurchased) {
        setCheckoutStatus("purchasing");
        const purchase = await purchaseExportUpgrade();

        if (!purchase.purchased) {
          if (purchase.outcome === "cancelled") {
            setNotice({ kind: "info", message: copy.notices.exportUpgradePurchaseCanceled });
          } else if (purchase.outcome === "pending") {
            setNotice({ kind: "info", message: copy.notices.exportUpgradePurchasePending });
          } else {
            setNotice({ kind: "error", message: copy.notices.exportUpgradeStatusFailed });
          }
          return;
        }

        setExportUpgradePurchased(true);
        setNotice({ kind: "success", message: copy.notices.exportUpgradePurchaseSuccess });
      }

      setCheckoutStatus("exporting");
      await downloadProjectZip(normalizedDesign, { access: "ios-unlimited" });
      setNotice({ kind: "success", message: copy.notices.nativeExportSuccess });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    } finally {
      setCheckoutStatus("idle");
    }
  };

  const restoreNativeExportUpgrade = async () => {
    if (checkoutStatus !== "idle") {
      return;
    }

    setCheckoutStatus("restoring");

    try {
      const restored = await restoreExportUpgrade();

      setExportUpgradePurchased(restored.purchased);
      setNotice({
        kind: restored.purchased ? "success" : "info",
        message: restored.purchased ? copy.notices.exportUpgradeRestored : copy.notices.exportUpgradeRestoreMissing
      });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.exportUpgradeStatusFailed) });
    } finally {
      setCheckoutStatus("idle");
    }
  };

  const saveDesignToDevice = () => {
    if (!isNativeIos) {
      return;
    }

    const updatedAt = new Date().toISOString();
    const record: SavedDesignRecord = {
      id: createId("saved-design"),
      name: createSavedDesignName(updatedAt, language),
      updatedAt,
      design: normalizedDesign
    };
    const removedDesign = savedDesigns.length >= savedDesignLimit
      ? savedDesigns[savedDesignLimit - 1]
      : null;

    if (removedDesign && !window.confirm(copy.editor.savedDesignLimitWarning(removedDesign.name))) {
      return;
    }

    const nextSavedDesigns = [
      record,
      ...savedDesigns
    ].slice(0, savedDesignLimit);

    try {
      persistSavedDesigns(nextSavedDesigns, copy.notices.deviceSaveFailed);
      setSavedDesigns(nextSavedDesigns);
      setNotice({ kind: "success", message: copy.notices.deviceDesignSaved(record.name) });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    }
  };

  const openSavedDesignFromDevice = (id: string) => {
    const record = savedDesigns.find((current) => current.id === id);

    if (!record) {
      setNotice({ kind: "error", message: copy.notices.deviceDesignMissing });
      return;
    }

    setDesign(normalizeDesign(record.design));
    setView("editor");
    setNotice({ kind: "success", message: copy.notices.deviceDesignOpened(record.name) });
  };

  const deleteSavedDesignFromDevice = (id: string) => {
    const record = savedDesigns.find((current) => current.id === id);

    if (!record) {
      setNotice({ kind: "error", message: copy.notices.deviceDesignMissing });
      return;
    }

    if (!window.confirm(copy.editor.deleteSavedDesignConfirm(record.name))) {
      return;
    }

    const nextSavedDesigns = savedDesigns.filter((current) => current.id !== id);

    try {
      persistSavedDesigns(nextSavedDesigns, copy.notices.deviceSaveFailed);
      setSavedDesigns(nextSavedDesigns);
      setNotice({ kind: "success", message: copy.notices.deviceDesignDeleted(record.name) });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    }
  };

  const completeCheckout = async (sessionId: string) => {
    setCheckoutStatus("verifying");

    try {
      const pending = loadPendingCheckout();

      if (!pending) {
        throw new Error(copy.notices.pendingDesignMissing);
      }

      const restoredDesign = normalizeDesign(pending.design);
      const restoredDesignHash = await hashDesign(restoredDesign);

      if (restoredDesignHash !== pending.designHash) {
        throw new Error(copy.notices.savedDesignMismatch);
      }

      const result = await verifyCheckoutSession(sessionId);

      if (result.entitlement.design_hash !== restoredDesignHash || result.entitlement.design_type !== hakofitDesignType) {
        throw new Error(copy.notices.paymentDesignMismatch);
      }

      setDesign(restoredDesign);
      setView("editor");
      await downloadProjectZip(restoredDesign, { entitlement: result.entitlement });
      clearPendingCheckout();
      setNotice({ kind: "success", message: copy.notices.paymentSuccess });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    } finally {
      setCheckoutStatus("idle");
      clearCheckoutQuery();
    }
  };

  const handleCheckoutCancel = () => {
    const pending = loadPendingCheckout();

    if (pending) {
      setDesign(normalizeDesign(pending.design));
      setView("editor");
    }

    clearPendingCheckout();
    clearCheckoutQuery();
    setNotice({ kind: "info", message: copy.notices.checkoutCanceled });
  };

  const restoreDesignFromFiles = async (files: FileList | null) => {
    try {
      const restoredDesign = await readDesignFromFiles(files, copy.notices.restoreDesignFileMissing);

      setDesign(restoredDesign);
      setView("editor");
      setNotice({ kind: "success", message: copy.notices.restoreDesignSuccess });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    }
  };

  const reexportFromFiles = async (files: FileList | null) => {
    if (checkoutStatus !== "idle") {
      return;
    }

    setCheckoutStatus("reexporting");

    try {
      const { design: restoredDesign, entitlement } = await readReexportFiles(files, copy.notices.reexportFilesMissing);
      const verification = await verifyEntitlement(entitlement);
      const validation = await validateReexport(restoredDesign, entitlement, verification);

      if (!validation.ok) {
        throw new Error(reexportValidationMessage(validation.reason, copy));
      }

      setDesign(restoredDesign);
      setView("editor");
      await downloadProjectZip(restoredDesign, { entitlement });
      setNotice({ kind: "success", message: copy.notices.reexportSuccess });
    } catch (error) {
      setNotice({ kind: "error", message: errorMessage(error, copy.notices.genericError) });
    } finally {
      setCheckoutStatus("idle");
    }
  };

  useEffect(() => {
    if (checkoutHandledRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get("checkout");

    if (checkoutResult === "success") {
      const sessionId = params.get("session_id");

      checkoutHandledRef.current = true;

      if (!sessionId) {
        setNotice({ kind: "error", message: copy.notices.checkoutSessionMissing });
        clearCheckoutQuery();
        return;
      }

      void completeCheckout(sessionId);
      return;
    }

    if (checkoutResult === "cancelled") {
      checkoutHandledRef.current = true;
      handleCheckoutCancel();
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const nextStaticPage = getStaticPageSlugFromPath(window.location.pathname);

      setStaticPageSlug(nextStaticPage);

      if (!nextStaticPage) {
        setView(rootView);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, [rootView]);

  if (staticPageSlug) {
    if (staticPageSlug === "contact") {
      return (
        <ContactPageView
          copy={copy}
          language={language}
          onHome={navigateHome}
          onLanguageChange={changeLanguage}
          onNavigateStaticPage={navigateStaticPage}
        />
      );
    }

    return (
      <LegalPageView
        copy={copy}
        language={language}
        page={getRuntimeLegalPage(localizedLegalPages[language][staticPageSlug], language, isNativeIos)}
        onHome={navigateHome}
        onLanguageChange={changeLanguage}
        onNavigateStaticPage={navigateStaticPage}
      />
    );
  }

  if (view === "landing") {
    return (
      <LandingView
        copy={copy}
        isNativeIos={isNativeIos}
        language={language}
        notice={notice}
        onLanguageChange={changeLanguage}
        onNavigateStaticPage={navigateStaticPage}
        onOpenCategories={() => setView("categories")}
      />
    );
  }

  if (view === "categories") {
    return (
      <CategoryView
        copy={copy}
        language={language}
        notice={notice}
        onBack={openAbout}
        onLanguageChange={changeLanguage}
        onSelect={(templateId) => chooseTemplate(templateId)}
      />
    );
  }

  return (
    <EditorView
      copy={copy}
      design={design}
      exportUpgradePurchased={exportUpgradePurchased}
      hasNumberFieldErrors={hasNumberFieldErrors}
      isNativeIos={isNativeIos}
      numberFieldErrors={numberFieldErrors}
      previewDesign={normalizedDesign}
      savedDesigns={savedDesigns}
      checkoutStatus={checkoutStatus}
      language={language}
      notice={notice}
      onBackToCategories={() => setView("categories")}
      onDeleteSavedDesign={deleteSavedDesignFromDevice}
      onOpenAbout={openAbout}
      onExport={exportDesign}
      onLanguageChange={changeLanguage}
      onOpenSavedDesign={openSavedDesignFromDevice}
      onReexport={reexportFromFiles}
      onRestoreExportUpgrade={restoreNativeExportUpgrade}
      onRestoreDesign={restoreDesignFromFiles}
      onSaveDesign={saveDesignToDevice}
      onUpdateDesign={updateDesign}
    />
  );
}

function LandingView({
  copy,
  isNativeIos,
  language,
  notice,
  onLanguageChange,
  onNavigateStaticPage,
  onOpenCategories
}: {
  copy: AppCopy;
  isNativeIos: boolean;
  language: Language;
  notice: Notice | null;
  onLanguageChange: (language: Language) => void;
  onNavigateStaticPage: (slug: PageSlug) => void;
  onOpenCategories: () => void;
}) {
  if (isNativeIos) {
    return (
      <NativeAboutView
        copy={copy}
        language={language}
        notice={notice}
        onLanguageChange={onLanguageChange}
        onNavigateStaticPage={onNavigateStaticPage}
        onOpenCategories={onOpenCategories}
      />
    );
  }

  return (
    <MarketingLandingView
      copy={copy}
      language={language}
      notice={notice}
      onLanguageChange={onLanguageChange}
      onNavigateStaticPage={onNavigateStaticPage}
      onOpenCategories={onOpenCategories}
    />
  );
}

function MarketingLandingView({
  copy,
  language,
  notice,
  onLanguageChange,
  onNavigateStaticPage,
  onOpenCategories
}: {
  copy: AppCopy;
  language: Language;
  notice: Notice | null;
  onLanguageChange: (language: Language) => void;
  onNavigateStaticPage: (slug: PageSlug) => void;
  onOpenCategories: () => void;
}) {
  const [heroTemplateIndex, setHeroTemplateIndex] = useState(0);
  const heroTemplates = useMemo(() => categories.map((category) => category.id), []);
  const heroDesign = useMemo<StorageDesign>(() => getTemplateDesign(heroTemplates[heroTemplateIndex]), [heroTemplateIndex, heroTemplates]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeroTemplateIndex((current) => (current + 1) % heroTemplates.length);
    }, 3600);

    return () => window.clearInterval(intervalId);
  }, [heroTemplates.length]);

  return (
    <main className="landing-shell">
      <TopNav copy={copy} onLogo={onOpenCategories} />
      <LanguageSwitch
        className="language-switch--floating"
        copy={copy}
        language={language}
        onChange={onLanguageChange}
      />
      {notice ? <div className={`notice notice--${notice.kind}`}>{notice.message}</div> : null}
      <section className="hero-grid">
        <div className="hero-copy">
          <h1>{copy.brand.name}</h1>
          <p>{copy.landing.heroDescription}</p>
          <div className="hero-actions">
            <button className="hero-cta" type="button" onClick={onOpenCategories}>
              {copy.landing.cta}
              <span>
                <ArrowRight size={24} />
              </span>
            </button>
          </div>
          <div className="hero-capabilities" aria-label={copy.landing.capabilitiesAriaLabel}>
            {copy.landing.capabilities.map((capability) => (
              <span key={capability}>{capability}</span>
            ))}
          </div>
        </div>
        <div className="hero-models" aria-hidden="true">
          <TrayScene autoRotate design={heroDesign} mode="hero" />
        </div>
      </section>
      <HowToSection copy={copy} />
      <UseCaseSection copy={copy} />
      <PricingSection copy={copy} />
      <FaqSection copy={copy} />
      <SiteFooter copy={copy} onNavigateStaticPage={onNavigateStaticPage} />
    </main>
  );
}

function NativeAboutView({
  copy,
  language,
  notice,
  onLanguageChange,
  onNavigateStaticPage,
  onOpenCategories
}: {
  copy: AppCopy;
  language: Language;
  notice: Notice | null;
  onLanguageChange: (language: Language) => void;
  onNavigateStaticPage: (slug: PageSlug) => void;
  onOpenCategories: () => void;
}) {
  const stepIcons = [Box, Printer];

  return (
    <main className="landing-shell native-about-shell">
      <header className="native-about-topbar">
        <button className="logo-button" type="button" onClick={onOpenCategories} aria-label={copy.editor.backToCategoriesLabel}>
          <Box size={22} />
        </button>
        <LanguageSwitch copy={copy} language={language} onChange={onLanguageChange} />
      </header>
      {notice ? <div className={`notice notice--${notice.kind}`}>{notice.message}</div> : null}
      <div className="native-about-content">
        <section className="native-about-hero" aria-labelledby="native-about-title">
          <p className="native-about-kicker">Hakofit</p>
          <h1 id="native-about-title">{copy.editor.aboutApp}</h1>
          <p>{copy.landing.heroDescription}</p>
        </section>

        <section className="native-about-section" aria-labelledby="native-howto-title">
          <h2 id="native-howto-title">{copy.landing.howToTitle}</h2>
          <div className="native-about-step-list">
            {copy.landing.howToSteps.map((step, index) => {
              const Icon = stepIcons[index] ?? Box;

              return (
                <div className="native-about-step" key={step.title}>
                  <span aria-hidden="true">
                    <Icon size={19} />
                  </span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.summary}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="native-about-section" aria-labelledby="native-pricing-title">
          <h2 id="native-pricing-title">{copy.landing.pricingTitle}</h2>
          <p className="native-about-section-lead">{copy.landing.pricingBody}</p>
          <dl className="native-about-list">
            {copy.landing.pricingItems.map((item) => (
              <div key={item.term}>
                <dt>{item.term}</dt>
                <dd>{item.description}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="native-about-section" aria-labelledby="native-faq-title">
          <h2 id="native-faq-title">{copy.landing.faqTitle}</h2>
          <div className="native-about-faq-list">
            {copy.landing.faqs.map((faq) => (
              <details className="native-about-faq" key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
      <footer className="native-about-footer">
        <nav aria-label={copy.footer.ariaLabel}>
          <FooterLink slug="contact" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links.contact}
          </FooterLink>
          <FooterLink slug="privacy" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links.privacy}
          </FooterLink>
          <FooterLink slug="terms" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links.terms}
          </FooterLink>
        </nav>
      </footer>
    </main>
  );
}

function HowToSection({ copy }: { copy: AppCopy }) {
  const stepIcons = [Box, Printer];

  return (
    <section className="howto-section" aria-labelledby="howto-title">
      <div className="howto-inner">
        <h2 id="howto-title">{copy.landing.howToTitle}</h2>
        <div className="howto-steps">
          {copy.landing.howToSteps.map((step, index) => {
            const Icon = stepIcons[index] ?? Box;

            return (
              <div className="howto-step" key={step.title}>
                <span className="howto-step__icon" aria-hidden="true">
                  <Icon size={24} />
                </span>
                <span className="howto-step__body">
                  <strong>{step.title}</strong>
                  <small>{step.summary}</small>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function UseCaseSection({ copy }: { copy: AppCopy }) {
  const useCaseIcons = [Box, CreditCard];

  return (
    <section className="seo-section usecase-section" aria-labelledby="usecase-title">
      <div className="seo-section__inner">
        <div className="seo-section__header">
          <h2 id="usecase-title">{copy.landing.useCaseTitle}</h2>
          <p>{copy.landing.useCaseBody}</p>
        </div>
        <div className="usecase-grid">
          {copy.landing.useCases.map((item, index) => {
            const Icon = useCaseIcons[index] ?? Box;

            return (
              <article className="usecase-card" key={item.title}>
                <span className="usecase-card__icon" aria-hidden="true">
                  <Icon size={24} />
                </span>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PricingSection({ copy }: { copy: AppCopy }) {
  return (
    <section className="seo-section pricing-section" aria-labelledby="pricing-title">
      <div className="seo-section__inner pricing-panel">
        <div>
          <h2 id="pricing-title">{copy.landing.pricingTitle}</h2>
          <p>{copy.landing.pricingBody}</p>
        </div>
        <dl className="pricing-list">
          {copy.landing.pricingItems.map((item) => (
            <div key={item.term}>
              <dt>{item.term}</dt>
              <dd>{item.description}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function FaqSection({ copy }: { copy: AppCopy }) {
  return (
    <section className="seo-section faq-section" aria-labelledby="faq-title">
      <div className="seo-section__inner">
        <div className="seo-section__header">
          <h2 id="faq-title">{copy.landing.faqTitle}</h2>
        </div>
        <div className="faq-list">
          {copy.landing.faqs.map((faq) => (
            <details className="faq-item" key={faq.question}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function SiteFooter({
  copy,
  onNavigateStaticPage
}: {
  copy: AppCopy;
  onNavigateStaticPage: (slug: PageSlug) => void;
}) {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <span>© 2026 Hakofit</span>
        <nav className="site-footer__links" aria-label={copy.footer.ariaLabel}>
          <FooterLink slug="contact" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links.contact}
          </FooterLink>
          <FooterLink slug="privacy" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links.privacy}
          </FooterLink>
          <FooterLink slug="terms" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links.terms}
          </FooterLink>
          <FooterLink slug="commercial-disclosure" onNavigateStaticPage={onNavigateStaticPage}>
            {copy.footer.links["commercial-disclosure"]}
          </FooterLink>
        </nav>
      </div>
    </footer>
  );
}

function FooterLink({
  children,
  onNavigateStaticPage,
  slug
}: {
  children: ReactNode;
  onNavigateStaticPage: (slug: PageSlug) => void;
  slug: PageSlug;
}) {
  return (
    <a
      href={pagePathMap[slug]}
      onClick={(event) => {
        event.preventDefault();
        onNavigateStaticPage(slug);
      }}
    >
      {children}
    </a>
  );
}

function CategoryView({
  copy,
  language,
  notice,
  onBack,
  onLanguageChange,
  onSelect
}: {
  copy: AppCopy;
  language: Language;
  notice: Notice | null;
  onBack: () => void;
  onLanguageChange: (language: Language) => void;
  onSelect: (templateId: TemplateId) => void;
}) {
  return (
    <main className="chooser-shell">
      <header className="compact-bar">
        <button className="logo-button" type="button" onClick={onBack} aria-label={copy.categories.backLabel}>
          <Box size={22} />
        </button>
        <LanguageSwitch copy={copy} language={language} onChange={onLanguageChange} />
      </header>
      {notice ? <div className={`notice notice--${notice.kind}`}>{notice.message}</div> : null}
      <section className="chooser-inner">
        <h1>{copy.categories.title}</h1>
        <div className="category-list">
          {categories.map((category) => (
            <CategoryCard copy={copy} key={category.id} category={category} onSelect={() => onSelect(category.id)} />
          ))}
        </div>
      </section>
    </main>
  );
}

function CategoryCard({ category, copy, onSelect }: { category: CategoryMeta; copy: AppCopy; onSelect: () => void }) {
  const Icon = category.icon;
  const previewDesign = useMemo(() => getTemplateDesign(category.id), [category.id]);
  const categoryCopy = copy.categories.items[category.id];

  return (
    <article className="category-card">
      <div className="category-preview" aria-hidden="true">
        <TrayScene design={previewDesign} mode="thumbnail" />
      </div>
      <div className="category-copy">
        <div className="category-title-row">
          <Icon size={24} />
          <h2>{categoryCopy.title}</h2>
        </div>
        <p>{categoryCopy.summary}</p>
        <button className="wide-create-button" type="button" onClick={onSelect}>
          {copy.categories.createLabel(categoryCopy.title)}
          <ArrowRight size={18} />
        </button>
      </div>
    </article>
  );
}

function EditorView({
  checkoutStatus,
  copy,
  design,
  exportUpgradePurchased,
  hasNumberFieldErrors,
  isNativeIos,
  language,
  notice,
  numberFieldErrors,
  onBackToCategories,
  onDeleteSavedDesign,
  onOpenAbout,
  onExport,
  onLanguageChange,
  onOpenSavedDesign,
  onReexport,
  onRestoreExportUpgrade,
  onRestoreDesign,
  onSaveDesign,
  onUpdateDesign,
  previewDesign,
  savedDesigns
}: {
  checkoutStatus: CheckoutStatus;
  copy: AppCopy;
  design: StorageDesign;
  exportUpgradePurchased: boolean;
  hasNumberFieldErrors: boolean;
  isNativeIos: boolean;
  language: Language;
  notice: Notice | null;
  numberFieldErrors: NumberFieldErrors;
  onBackToCategories: () => void;
  onDeleteSavedDesign: (id: string) => void;
  onOpenAbout: () => void;
  onExport: () => void;
  onLanguageChange: (language: Language) => void;
  onOpenSavedDesign: (id: string) => void;
  onReexport: (files: FileList | null) => void;
  onRestoreExportUpgrade: () => void;
  onRestoreDesign: (files: FileList | null) => void;
  onSaveDesign: () => void;
  onUpdateDesign: (patch: Partial<StorageDesign>) => void;
  previewDesign: StorageDesign;
  savedDesigns: SavedDesignRecord[];
}) {
  const [selectedItem, setSelectedItem] = useState<Selection | null>(null);
  const [activeTool, setActiveTool] = useState<EditorTool | null>(null);
  const [cameraViewMode, setCameraViewMode] = useState<CameraViewMode>(null);
  const [cameraViewRevision, setCameraViewRevision] = useState(0);
  const [activeHandleFace, setActiveHandleFace] = useState<WallFace>("front");
  const [isToolDragging, setIsToolDragging] = useState(false);
  const [isModelDragging, setIsModelDragging] = useState(false);
  const [isMobileChromeOpen, setIsMobileChromeOpen] = useState(false);
  const [isSavedDesignsOpen, setIsSavedDesignsOpen] = useState(false);
  const restoreInputRef = useRef<HTMLInputElement | null>(null);
  const reexportInputRef = useRef<HTMLInputElement | null>(null);
  const isMobileEditor = useMediaQuery("(max-width: 820px), (hover: none) and (pointer: coarse)");
  const isCheckoutBusy = checkoutStatus !== "idle";
  const fieldLimits = getNumberFieldLimits(design);
  const checkoutButtonLabel = checkoutStatus === "creating"
    ? copy.editor.checkout.creating
    : checkoutStatus === "verifying"
      ? copy.editor.checkout.verifying
      : checkoutStatus === "reexporting"
        ? copy.editor.checkout.reexporting
        : checkoutStatus === "purchasing"
          ? copy.editor.checkout.upgrading
          : checkoutStatus === "exporting"
            ? copy.editor.checkout.exporting
            : isNativeIos
              ? exportUpgradePurchased
                ? copy.editor.checkout.exportReady
                : copy.editor.checkout.upgrade
              : copy.editor.checkout.download;

  useEffect(() => {
    if (!isMobileEditor) {
      setIsMobileChromeOpen(false);
    }
  }, [isMobileEditor]);

  useEffect(() => {
    if (!selectedItem) {
      return;
    }

    if (selectedItem.type === "handle") {
      const exists = design.handlePlacements.some((placement) => placement.id === selectedItem.id);

      if (!exists) {
        setSelectedItem(null);
      }
      return;
    }

    const exists = design.dividerSegments.some((segment) => segment.id === selectedItem.id);

    if (!exists) {
      setSelectedItem(null);
    }
  }, [design.dividerSegments, design.handlePlacements, selectedItem]);

  const updateHandleStyle = (handleStyle: StorageDesign["handleStyle"]) => {
    if (handleStyle === "none") {
      onUpdateDesign({ handleStyle, hasFingerNotch: false, handlePlacements: [] });
      setSelectedItem(null);
      return;
    }

    const handlePlacements = design.handlePlacements.length > 0
      ? design.handlePlacements
      : [{ id: createId("handle-right"), face: "right" as WallFace, offset: 0.5, verticalOffset: 0.58 }];

    onUpdateDesign({ handleStyle, hasFingerNotch: true, handlePlacements });
  };

  const addHandle = (point: PointRatio) => {
    const face = point.face ?? nearestFace(point);
    const offset = face === "left" || face === "right" ? point.y : point.x;
    const existing = design.handlePlacements.find((placement) => placement.face === face);
    const placement: HandlePlacement = {
      id: existing?.id ?? createId(`handle-${face}`),
      face,
      offset,
      verticalOffset: point.vertical ?? existing?.verticalOffset ?? 0.58
    };
    const handlePlacements = [
      ...design.handlePlacements.filter((current) => current.face !== face),
      placement
    ];
    const handleSide = face === "left" || face === "right" ? "short" : "long";

    onUpdateDesign({
      handleStyle: design.handleStyle === "none" ? "slot" : design.handleStyle,
      hasFingerNotch: true,
      handleSide,
      handleCount: handlePlacements.filter((current) =>
        handleSide === "short"
          ? current.face === "left" || current.face === "right"
          : current.face === "front" || current.face === "back"
      ).length,
      handleOffset: offset,
      handlePlacements
    });
    setSelectedItem({ type: "handle", id: placement.id });
  };

  const addDivider = (axis: DividerAxis, point: PointRatio) => {
    const segment: DividerSegment = {
      id: createId(`divider-${axis}`),
      axis,
      fraction: axis === "column" ? point.x : point.y,
      startFraction: 0,
      endFraction: 1
    };
    const dividerSegments = [...design.dividerSegments, segment];

    onUpdateDesign({
      dividerSegments,
      columns: 1 + dividerSegments.filter((current) => current.axis === "column").length,
      rows: 1 + dividerSegments.filter((current) => current.axis === "row").length,
      columnFractions: sortedFractions(dividerSegments.filter((current) => current.axis === "column").map((current) => current.fraction)),
      rowFractions: sortedFractions(dividerSegments.filter((current) => current.axis === "row").map((current) => current.fraction))
    });
    setSelectedItem({ type: "divider", id: segment.id });
  };

  const moveHandle = (id: string, point: PointRatio) => {
    const handlePlacements = design.handlePlacements.map((placement) => {
      if (placement.id !== id) {
        return placement;
      }

      return {
        ...placement,
        offset: placement.face === "left" || placement.face === "right" ? point.y : point.x,
        verticalOffset: point.vertical ?? placement.verticalOffset ?? 0.58
      };
    });
    const moved = handlePlacements.find((placement) => placement.id === id);

    onUpdateDesign({ handlePlacements, handleOffset: moved?.offset ?? design.handleOffset });
  };

  const moveHandleByOffset = (id: string, offset: number, verticalOffset?: number) => {
    const placement = design.handlePlacements.find((current) => current.id === id);

    if (!placement) {
      return;
    }

    moveHandle(id, placement.face === "left" || placement.face === "right"
      ? { x: 0.5, y: offset, vertical: verticalOffset }
      : { x: offset, y: 0.5, vertical: verticalOffset });
  };

  const moveDivider = (id: string, point: PointRatio) => {
    const movedSegment = design.dividerSegments.find((segment) => segment.id === id);

    if (!movedSegment) {
      return;
    }

    const nextFraction = movedSegment.axis === "column" ? point.x : point.y;
    const previousFraction = movedSegment.fraction;
    const dividerSegments = design.dividerSegments.map((segment) => {
      if (segment.id !== id) {
        if (segment.axis === movedSegment.axis) {
          return segment;
        }

        return {
          ...segment,
          startFraction: isSameFraction(segment.startFraction, previousFraction) ? nextFraction : segment.startFraction,
          endFraction: isSameFraction(segment.endFraction, previousFraction) ? nextFraction : segment.endFraction
        };
      }

      return {
        ...segment,
        fraction: nextFraction
      };
    });

    onUpdateDesign({
      dividerSegments,
      columnFractions: sortedFractions(dividerSegments.filter((segment) => segment.axis === "column").map((segment) => segment.fraction)),
      rowFractions: sortedFractions(dividerSegments.filter((segment) => segment.axis === "row").map((segment) => segment.fraction))
    });
  };

  const moveDividerByFraction = (id: string, fraction: number) => {
    const segment = design.dividerSegments.find((current) => current.id === id);

    if (!segment) {
      return;
    }

    moveDivider(id, segment.axis === "column" ? { x: toRatio(fraction), y: 0.5 } : { x: 0.5, y: toRatio(fraction) });
  };

  const handleDropTool = (tool: EditorTool, point: PointRatio) => {
    if (tool === "handle") {
      addHandle(point);
    } else {
      addDivider(tool === "divider-column" ? "column" : "row", point);
    }

    setActiveTool(null);
    setIsToolDragging(false);
  };

  const focusToolView = (tool: EditorTool) => {
    setCameraViewMode(tool === "handle" ? "handle" : "top");
    setCameraViewRevision((current) => current + 1);
  };

  const selectTool = (tool: EditorTool | null) => {
    setActiveTool(tool);

    if (!tool) {
      setIsToolDragging(false);
      return;
    }

    focusToolView(tool);
  };

  const deleteSelection = () => {
    if (!selectedItem) {
      return;
    }

    if (selectedItem.type === "handle") {
      const handlePlacements = design.handlePlacements.filter((placement) => placement.id !== selectedItem.id);
      const nextHandle = handlePlacements[0];

      onUpdateDesign({
        handleStyle: handlePlacements.length > 0 ? design.handleStyle : "none",
        hasFingerNotch: handlePlacements.length > 0,
        handleCount: handlePlacements.length,
        handleOffset: nextHandle?.offset ?? design.handleOffset,
        handlePlacements
      });
      setSelectedItem(null);
      return;
    }

    const dividerSegments = design.dividerSegments.filter((segment) => segment.id !== selectedItem.id);

    onUpdateDesign({
      dividerSegments,
      columns: 1 + dividerSegments.filter((segment) => segment.axis === "column").length,
      rows: 1 + dividerSegments.filter((segment) => segment.axis === "row").length,
      columnFractions: sortedFractions(dividerSegments.filter((segment) => segment.axis === "column").map((segment) => segment.fraction)),
      rowFractions: sortedFractions(dividerSegments.filter((segment) => segment.axis === "row").map((segment) => segment.fraction))
    });
    setSelectedItem(null);
  };

  const splitSelectedDivider = () => {
    if (selectedItem?.type !== "divider") {
      return;
    }

    const segment = design.dividerSegments.find((current) => current.id === selectedItem.id);

    if (!segment || !canSplitDividerSegment(design.dividerSegments, segment)) {
      return;
    }

    const splitAxis = segment.axis === "row" ? "column" : "row";
    const breaks = getDividerBreaks(design.dividerSegments, splitAxis)
      .filter((fraction) => fraction > segment.startFraction && fraction < segment.endFraction);

    if (breaks.length === 0) {
      return;
    }

    const splitPoints = [segment.startFraction, ...breaks, segment.endFraction];
    const splitSegments = splitPoints.slice(0, -1).map((startFraction, index) => ({
      ...segment,
      id: createId(`${segment.id}-split-${index}`),
      startFraction,
      endFraction: splitPoints[index + 1]
    }));
    const dividerSegments = design.dividerSegments.flatMap((current) =>
      current.id === segment.id ? splitSegments : [current]
    );

    onUpdateDesign({
      dividerSegments,
      columns: 1 + dividerSegments.filter((current) => current.axis === "column").length,
      rows: 1 + dividerSegments.filter((current) => current.axis === "row").length,
      columnFractions: sortedFractions(dividerSegments.filter((current) => current.axis === "column").map((current) => current.fraction)),
      rowFractions: sortedFractions(dividerSegments.filter((current) => current.axis === "row").map((current) => current.fraction))
    });
    setSelectedItem({ type: "divider", id: splitSegments[0].id });
  };

  const selectedDivider = selectedItem?.type === "divider"
    ? design.dividerSegments.find((segment) => segment.id === selectedItem.id)
    : null;
  const selectedDividerHasSplitBreaks = selectedDivider ? hasDividerSplitBreaks(design.dividerSegments, selectedDivider) : false;
  const canSplitSelectedDivider = selectedDivider
    ? selectedDividerHasSplitBreaks && canSplitDividerSegment(design.dividerSegments, selectedDivider)
    : false;
  const shouldShowSplitSelectedDivider = Boolean(
    selectedDivider &&
    (selectedDividerHasSplitBreaks || hasOppositeAxisSplitSegment(design.dividerSegments, selectedDivider.axis))
  );

  const moveHandleToFace = (id: string, face: WallFace) => {
    const placement = design.handlePlacements.find((current) => current.id === id);

    if (!placement || placement.face === face) {
      return;
    }

    const handlePlacements = design.handlePlacements
      .filter((current) => current.id === id || current.face !== face)
      .map((current) => current.id === id ? { ...current, face } : current);
    const handleSide = face === "left" || face === "right" ? "short" : "long";

    onUpdateDesign({
      handlePlacements,
      handleOffset: placement.offset,
      handleSide,
      handleCount: handlePlacements.filter((current) =>
        handleSide === "short"
          ? current.face === "left" || current.face === "right"
          : current.face === "front" || current.face === "back"
      ).length
    });
    setActiveHandleFace(face);
    setCameraViewMode("handle");
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        !selectedItem ||
        (event.key !== "Delete" && event.key !== "Backspace") ||
        target?.closest("input, textarea, [contenteditable='true']")
      ) {
        return;
      }

      event.preventDefault();
      deleteSelection();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteSelection, selectedItem]);

  return (
    <main className="editor-shell">
      <button
        aria-controls="editor-mobile-actions"
        aria-expanded={isMobileChromeOpen}
        aria-label={isMobileChromeOpen ? copy.editor.hideToolbar : copy.editor.showToolbar}
        className="mobile-editor-menu-button"
        type="button"
        onClick={() => setIsMobileChromeOpen((current) => !current)}
      >
        {isMobileChromeOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      <header
        className={`editor-topbar${isNativeIos ? " editor-topbar--native" : ""}${isMobileChromeOpen ? " editor-topbar--open" : ""}`}
        hidden={isMobileEditor && !isMobileChromeOpen}
        id="editor-mobile-actions"
      >
        <div className="editor-left">
          <button className="logo-button" type="button" onClick={onBackToCategories} aria-label={copy.editor.backToCategoriesLabel}>
            <Box size={22} />
          </button>
        </div>
        <div className="editor-actions">
          <LanguageSwitch copy={copy} language={language} onChange={onLanguageChange} />
          {isNativeIos ? (
            <button className="secondary-action-button editor-action-button--about" type="button" disabled={isCheckoutBusy} onClick={onOpenAbout}>
              <Info size={17} />
              {copy.editor.aboutApp}
            </button>
          ) : null}
          {!isNativeIos ? (
            <input
              accept=".json,application/json"
              className="file-input"
              ref={restoreInputRef}
              type="file"
              onChange={(event) => {
                onRestoreDesign(event.currentTarget.files);
                event.currentTarget.value = "";
              }}
            />
          ) : null}
          {!isNativeIos ? (
            <input
              accept=".json,application/json"
              className="file-input"
              multiple
              ref={reexportInputRef}
              type="file"
              onChange={(event) => {
                onReexport(event.currentTarget.files);
                event.currentTarget.value = "";
              }}
            />
          ) : null}
          {isNativeIos ? (
            <>
              <button className="secondary-action-button editor-action-button--save" type="button" onClick={onSaveDesign}>
                <Save size={17} />
                {copy.editor.saveDesign}
              </button>
              <button className="secondary-action-button editor-action-button--saved" type="button" onClick={() => setIsSavedDesignsOpen(true)}>
                <FolderOpen size={17} />
                {copy.editor.savedDesigns}
              </button>
              <button className="secondary-action-button editor-action-button--restore-purchase" type="button" disabled={isCheckoutBusy} onClick={onRestoreExportUpgrade}>
                <RefreshCw size={17} />
                {checkoutStatus === "restoring" ? copy.editor.checkout.restoringPurchase : copy.editor.checkout.restorePurchase}
              </button>
            </>
          ) : (
            <>
              <button className="secondary-action-button" type="button" disabled={isCheckoutBusy} onClick={() => restoreInputRef.current?.click()}>
                <Upload size={17} />
                {copy.editor.restoreDesign}
              </button>
              <button className="secondary-action-button" type="button" disabled={isCheckoutBusy} onClick={() => reexportInputRef.current?.click()}>
                <RefreshCw size={17} />
                {checkoutStatus === "reexporting" ? copy.editor.checkout.reexporting : copy.editor.checkout.reexport}
              </button>
            </>
          )}
          <button
            className={`export-button${hasNumberFieldErrors ? " export-button--invalid" : ""}`}
            type="button"
            disabled={isCheckoutBusy || hasNumberFieldErrors}
            title={hasNumberFieldErrors ? copy.editor.validation.fixBeforeDownload : undefined}
            onClick={onExport}
          >
            {isNativeIos && exportUpgradePurchased ? <Download size={18} /> : <CreditCard size={18} />}
            {checkoutButtonLabel}
          </button>
        </div>
      </header>
      {isNativeIos && isSavedDesignsOpen ? (
        <SavedDesignsSheet
          copy={copy}
          language={language}
          savedDesigns={savedDesigns}
          onClose={() => setIsSavedDesignsOpen(false)}
          onDelete={onDeleteSavedDesign}
          onOpen={(id) => {
            onOpenSavedDesign(id);
            setIsSavedDesignsOpen(false);
          }}
        />
      ) : null}

      <aside className="editor-panel">
        <section className="accordion-section">
          <div className="accordion-title">{copy.editor.sections.size}</div>
          <div className="field-grid">
            <NumberField
              decreaseLabel={copy.numberField.decrease}
              error={numberFieldErrors.widthMm}
              increaseLabel={copy.numberField.increase}
              label={copy.editor.fields.width}
              value={design.widthMm}
              {...fieldLimits.widthMm}
              onChange={(widthMm) => onUpdateDesign({ widthMm })}
            />
            <NumberField
              decreaseLabel={copy.numberField.decrease}
              error={numberFieldErrors.depthMm}
              increaseLabel={copy.numberField.increase}
              label={copy.editor.fields.depth}
              value={design.depthMm}
              {...fieldLimits.depthMm}
              onChange={(depthMm) => onUpdateDesign({ depthMm })}
            />
            <NumberField
              decreaseLabel={copy.numberField.decrease}
              error={numberFieldErrors.heightMm}
              increaseLabel={copy.numberField.increase}
              label={copy.editor.fields.height}
              value={design.heightMm}
              {...fieldLimits.heightMm}
              onChange={(heightMm) => onUpdateDesign({ heightMm })}
            />
          </div>
        </section>

        <section className="accordion-section">
          <div className="accordion-title">{copy.editor.sections.basics}</div>
          <SegmentedRow
            label={copy.editor.fields.bottom}
            options={[
              { label: copy.editor.bottomOptions.withBottom, active: design.hasBottom, onClick: () => onUpdateDesign({ hasBottom: true }) },
              { label: copy.editor.bottomOptions.withoutBottom, active: !design.hasBottom, onClick: () => onUpdateDesign({ hasBottom: false }) }
            ]}
          />
          <NumberField
            decreaseLabel={copy.numberField.decrease}
            error={numberFieldErrors.wallMm}
            increaseLabel={copy.numberField.increase}
            label={copy.editor.fields.wallThickness}
            value={design.wallMm}
            {...fieldLimits.wallMm}
            onChange={(wallMm) => onUpdateDesign({ wallMm })}
          />
          <NumberField
            decreaseLabel={copy.numberField.decrease}
            error={numberFieldErrors.bottomMm}
            increaseLabel={copy.numberField.increase}
            label={copy.editor.fields.bottomThickness}
            value={design.bottomMm}
            {...fieldLimits.bottomMm}
            onChange={(bottomMm) => onUpdateDesign({ bottomMm })}
          />
          <NumberField
            decreaseLabel={copy.numberField.decrease}
            error={numberFieldErrors.cornerRadiusMm}
            increaseLabel={copy.numberField.increase}
            label={copy.editor.fields.cornerRadius}
            value={design.cornerRadiusMm}
            {...fieldLimits.cornerRadiusMm}
            onChange={(cornerRadiusMm) => onUpdateDesign({ cornerRadiusMm })}
          />
        </section>

        <ToolPalette
          activeTool={activeTool}
          canSplitSelectedDivider={canSplitSelectedDivider}
          copy={copy}
          design={design}
          fieldErrors={numberFieldErrors}
          fieldLimits={fieldLimits}
          selectedItem={selectedItem}
          showSplitSelectedDivider={shouldShowSplitSelectedDivider}
          onDeleteSelection={deleteSelection}
          onSelectTool={selectTool}
          onSplitSelectedDivider={splitSelectedDivider}
          onToolDraggingChange={setIsToolDragging}
          isMobileEditor={isMobileEditor}
          onMoveDivider={moveDividerByFraction}
          onMoveHandle={moveHandleByOffset}
          onMoveHandleFace={moveHandleToFace}
          onUpdateDesign={onUpdateDesign}
          onUpdateHandleStyle={updateHandleStyle}
        />
      </aside>

      <section className="editor-workspace">
        <div className="model-stage">
          <TrayScene
            activeTool={activeTool}
            cameraViewMode={cameraViewMode}
            cameraViewRevision={cameraViewRevision}
            design={previewDesign}
            controlsEnabled={!isModelDragging && !isToolDragging}
            handleDropFace={activeHandleFace}
            interactionsEnabled={!activeTool}
            onDropTool={handleDropTool}
            selectedItem={selectedItem}
            onDraggingChange={setIsModelDragging}
            onMoveDivider={moveDividerByFraction}
            onMoveHandle={moveHandleByOffset}
            onSelectItem={setSelectedItem}
            onToolComplete={() => setActiveTool(null)}
          />
        </div>
        <ModelInteractionLayer
          copy={copy}
          design={previewDesign}
          activeTool={activeTool}
          handleFace={activeHandleFace}
          isMobileEditor={isMobileEditor}
          isToolDragging={isToolDragging}
          onHandleFaceChange={setActiveHandleFace}
          onDropTool={handleDropTool}
          onSelectTool={(tool) => {
            selectTool(tool);
            setIsToolDragging(false);
          }}
          onSelect={setSelectedItem}
        />
        {notice ? <div className={`notice notice--${notice.kind}`}>{notice.message}</div> : null}
      </section>
    </main>
  );
}

function SavedDesignsSheet({
  copy,
  language,
  savedDesigns,
  onClose,
  onDelete,
  onOpen
}: {
  copy: AppCopy;
  language: Language;
  savedDesigns: SavedDesignRecord[];
  onClose: () => void;
  onDelete: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="saved-designs-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby="saved-designs-title"
        aria-modal="true"
        className="saved-designs-sheet"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="saved-designs-header">
          <h2 id="saved-designs-title">{copy.editor.savedDesignsTitle}</h2>
          <button className="saved-designs-close" type="button" onClick={onClose} aria-label={copy.editor.closeSavedDesigns}>
            <X size={19} />
          </button>
        </header>
        {savedDesigns.length === 0 ? (
          <p className="saved-designs-empty">{copy.editor.noSavedDesigns}</p>
        ) : (
          <div className="saved-designs-list">
            {savedDesigns.map((record) => (
              <div className="saved-design-row" key={record.id}>
                <div className="saved-design-row__summary">
                  <span>
                    <strong>{record.name}</strong>
                    <small>{formatSavedDesignDate(record.updatedAt, language)}</small>
                  </span>
                  <span className="saved-design-row__meta">
                    {record.design.widthMm} x {record.design.depthMm} x {record.design.heightMm} mm
                  </span>
                </div>
                <div className="saved-design-row__actions">
                  <button
                    className="saved-design-row__open"
                    type="button"
                    aria-label={copy.editor.openSavedDesignLabel(record.name)}
                    onClick={() => onOpen(record.id)}
                  >
                    <FolderOpen size={16} />
                    {copy.editor.openSavedDesign}
                  </button>
                  <button
                    className="saved-design-row__delete"
                    type="button"
                    aria-label={copy.editor.deleteSavedDesignLabel(record.name)}
                    onClick={() => onDelete(record.id)}
                  >
                    <Trash2 size={16} />
                    {copy.editor.deleteSavedDesign}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TopNav({ copy, onLogo }: { copy: AppCopy; onLogo: () => void }) {
  return (
    <header className="top-nav">
      <button className="nav-brand" type="button" onClick={onLogo} aria-label={copy.brand.logoLabel}>
        <span className="brand-cube">
          <Box size={23} />
        </span>
        <span>
          {copy.brand.name}
          <small>{copy.brand.tagline}</small>
        </span>
      </button>
    </header>
  );
}

function LanguageSwitch({
  className = "",
  copy,
  language,
  onChange
}: {
  className?: string;
  copy: AppCopy;
  language: Language;
  onChange: (language: Language) => void;
}) {
  return (
    <div className={`language-switch ${className}`} role="group" aria-label={copy.languageSwitch.ariaLabel}>
      <button
        className={language === "ja" ? "is-active" : ""}
        lang="ja"
        type="button"
        onClick={() => onChange("ja")}
      >
        {copy.languageSwitch.ja}
      </button>
      <button
        className={language === "en" ? "is-active" : ""}
        lang="en"
        type="button"
        onClick={() => onChange("en")}
      >
        {copy.languageSwitch.en}
      </button>
    </div>
  );
}

function LegalPageView({
  copy,
  language,
  onHome,
  onLanguageChange,
  onNavigateStaticPage,
  page
}: {
  copy: AppCopy;
  language: Language;
  onHome: () => void;
  onLanguageChange: (language: Language) => void;
  onNavigateStaticPage: (slug: PageSlug) => void;
  page: LegalPageContent;
}) {
  return (
    <main className="legal-shell">
      <header className="legal-topbar">
        <a
          className="legal-brand"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            onHome();
          }}
        >
          <span className="brand-cube">
            <Box size={23} />
          </span>
          <span>
            {copy.brand.name}
            <small>{copy.brand.tagline}</small>
          </span>
        </a>
        <LanguageSwitch copy={copy} language={language} onChange={onLanguageChange} />
      </header>
      <article className="legal-document">
        <p className="legal-kicker">Hakofit</p>
        <h1>{page.title}</h1>
        {page.lead ? <p className="legal-lead">{page.lead}</p> : null}
        <p className="legal-date">{page.effectiveDate}</p>
        <div className="legal-sections">
          {page.sections.map((section) => (
            <section className="legal-section" key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.items ? (
                <ul>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {section.rows ? (
                <div className="legal-table-wrap">
                  <table className="legal-table">
                    <tbody>
                      {section.rows.map((row) => (
                        <tr key={row.label}>
                          <th scope="row">{row.label}</th>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          ))}
        </div>
      </article>
      <SiteFooter copy={copy} onNavigateStaticPage={onNavigateStaticPage} />
    </main>
  );
}

function ContactPageView({
  copy,
  language,
  onHome,
  onLanguageChange,
  onNavigateStaticPage
}: {
  copy: AppCopy;
  language: Language;
  onHome: () => void;
  onLanguageChange: (language: Language) => void;
  onNavigateStaticPage: (slug: PageSlug) => void;
}) {
  return (
    <main className="legal-shell">
      <header className="legal-topbar">
        <a
          className="legal-brand"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            onHome();
          }}
        >
          <span className="brand-cube">
            <Box size={23} />
          </span>
          <span>
            {copy.brand.name}
            <small>{copy.brand.tagline}</small>
          </span>
        </a>
        <LanguageSwitch copy={copy} language={language} onChange={onLanguageChange} />
      </header>
      <article className="contact-document">
        <p className="legal-kicker">Hakofit</p>
        <h1>{copy.contact.title}</h1>
        <p className="legal-lead">{copy.contact.lead}</p>
        <div className="contact-card">
          <p className="contact-email">{contactEmail}</p>
          <p className="contact-note">{copy.contact.note}</p>
          <div className="contact-actions">
            <a className="contact-submit" href={`mailto:${contactEmail}?subject=${encodeURIComponent(copy.contact.mailSubject)}`}>
              {copy.contact.mailButton}
            </a>
          </div>
        </div>
      </article>
      <SiteFooter copy={copy} onNavigateStaticPage={onNavigateStaticPage} />
    </main>
  );
}

function SegmentedRow({
  label,
  options
}: {
  label: string;
  options: Array<{ label: string; active: boolean; onClick: () => void }>;
}) {
  return (
    <div className="segmented-row">
      <span>{label}</span>
      <div className="segmented-control">
        {options.map((option) => (
          <button
            key={option.label}
            className={option.active ? "is-active" : ""}
            type="button"
            onClick={option.onClick}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ToolPalette({
  activeTool,
  canSplitSelectedDivider,
  copy,
  design,
  fieldErrors,
  fieldLimits,
  isMobileEditor,
  selectedItem,
  showSplitSelectedDivider,
  onDeleteSelection,
  onSelectTool,
  onSplitSelectedDivider,
  onToolDraggingChange,
  onMoveDivider,
  onMoveHandle,
  onMoveHandleFace,
  onUpdateDesign,
  onUpdateHandleStyle
}: {
  activeTool: EditorTool | null;
  canSplitSelectedDivider: boolean;
  copy: AppCopy;
  design: StorageDesign;
  fieldErrors: NumberFieldErrors;
  fieldLimits: Record<EditableNumberField, NumberFieldLimits>;
  isMobileEditor: boolean;
  selectedItem: Selection | null;
  showSplitSelectedDivider: boolean;
  onDeleteSelection: () => void;
  onSelectTool: (tool: EditorTool | null) => void;
  onSplitSelectedDivider: () => void;
  onToolDraggingChange: (dragging: boolean) => void;
  onMoveDivider: (id: string, fraction: number) => void;
  onMoveHandle: (id: string, offset: number, verticalOffset?: number) => void;
  onMoveHandleFace: (id: string, face: WallFace) => void;
  onUpdateDesign: (patch: Partial<StorageDesign>) => void;
  onUpdateHandleStyle: (handleStyle: StorageDesign["handleStyle"]) => void;
}) {
  return (
    <section className="accordion-section tool-section">
      <div className="accordion-title">{copy.editor.sections.add}</div>
      <div className="tool-palette">
        <ToolButton
          active={activeTool === "handle"}
          copy={copy}
          icon={GripHorizontal}
          isMobileEditor={isMobileEditor}
          label={copy.editor.tools.handle}
          onSelectTool={onSelectTool}
          onToolDraggingChange={onToolDraggingChange}
          tool="handle"
        />
        <ToolButton
          active={activeTool === "divider-column"}
          copy={copy}
          icon={SeparatorVertical}
          isMobileEditor={isMobileEditor}
          label={copy.editor.tools.verticalDivider}
          onSelectTool={onSelectTool}
          onToolDraggingChange={onToolDraggingChange}
          tool="divider-column"
        />
        <ToolButton
          active={activeTool === "divider-row"}
          copy={copy}
          icon={SeparatorHorizontal}
          isMobileEditor={isMobileEditor}
          label={copy.editor.tools.horizontalDivider}
          onSelectTool={onSelectTool}
          onToolDraggingChange={onToolDraggingChange}
          tool="divider-row"
        />
      </div>
      {showSplitSelectedDivider ? (
        <button
          className="split-selection-button"
          type="button"
          disabled={!canSplitSelectedDivider}
          onClick={onSplitSelectedDivider}
        >
          {copy.editor.splitSelection}
        </button>
      ) : null}
      {selectedItem ? (
        <button className="delete-selection-button" type="button" onClick={onDeleteSelection}>
          <Trash2 size={18} />
          {copy.editor.deleteSelection}
        </button>
      ) : null}
      <MobileSelectionControls
        copy={copy}
        design={design}
        selectedItem={selectedItem}
        onMoveDivider={onMoveDivider}
        onMoveHandle={onMoveHandle}
        onMoveHandleFace={onMoveHandleFace}
      />
      {activeTool ? (
        <div className="tool-help" role="status">
          {activeTool === "handle" ? (
            <>
              <span className="copy-desktop">{copy.editor.toolHelp.handleDesktop}</span>
              <span className="copy-mobile">{copy.editor.toolHelp.mobile}</span>
            </>
          ) : (
            <>
              <span className="copy-desktop">{copy.editor.toolHelp.dividerDesktop}</span>
              <span className="copy-mobile">{copy.editor.toolHelp.mobile}</span>
            </>
          )}
        </div>
      ) : null}
      <HandleControls copy={copy} design={design} onUpdateStyle={onUpdateHandleStyle} />
      {design.handleStyle !== "none" ? (
        <NumberField
          decreaseLabel={copy.numberField.decrease}
          error={fieldErrors.handleLengthMm}
          increaseLabel={copy.numberField.increase}
          label={copy.editor.fields.handleSize}
          value={design.handleLengthMm}
          {...fieldLimits.handleLengthMm}
          onChange={(handleLengthMm) => onUpdateDesign({ handleLengthMm })}
        />
      ) : null}
      <NumberField
        decreaseLabel={copy.numberField.decrease}
        error={fieldErrors.dividerMm}
        increaseLabel={copy.numberField.increase}
        label={copy.editor.fields.dividerThickness}
        value={design.dividerMm}
        {...fieldLimits.dividerMm}
        onChange={(dividerMm) => onUpdateDesign({ dividerMm })}
      />
      <NumberField
        decreaseLabel={copy.numberField.decrease}
        error={fieldErrors.dividerHeightMm}
        increaseLabel={copy.numberField.increase}
        label={copy.editor.fields.dividerHeight}
        value={design.dividerHeightMm}
        {...fieldLimits.dividerHeightMm}
        onChange={(dividerHeightMm) => onUpdateDesign({ dividerHeightMm })}
      />
    </section>
  );
}

function MobileSelectionControls({
  copy,
  design,
  selectedItem,
  onMoveDivider,
  onMoveHandle,
  onMoveHandleFace
}: {
  copy: AppCopy;
  design: StorageDesign;
  selectedItem: Selection | null;
  onMoveDivider: (id: string, fraction: number) => void;
  onMoveHandle: (id: string, offset: number, verticalOffset?: number) => void;
  onMoveHandleFace: (id: string, face: WallFace) => void;
}) {
  if (!selectedItem) {
    return null;
  }

  if (selectedItem.type === "divider") {
    const divider = design.dividerSegments.find((segment) => segment.id === selectedItem.id);

    if (!divider) {
      return null;
    }

    return (
      <div className="mobile-selection-controls" aria-label={copy.editor.mobileSelection.dividerAriaLabel}>
        <RangeField
          endLabel={divider.axis === "column" ? copy.editor.mobileSelection.right : copy.editor.mobileSelection.front}
          label={divider.axis === "column" ? copy.editor.mobileSelection.horizontalPosition : copy.editor.mobileSelection.depthPosition}
          max={designLimits.handleOffset.max}
          min={designLimits.handleOffset.min}
          step={designLimits.handleOffset.step}
          startLabel={divider.axis === "column" ? copy.editor.mobileSelection.left : copy.editor.mobileSelection.back}
          value={divider.fraction}
          onChange={(fraction) => onMoveDivider(divider.id, fraction)}
        />
      </div>
    );
  }

  const placement = design.handlePlacements.find((current) => current.id === selectedItem.id);

  if (!placement) {
    return null;
  }

  const [startLabel, endLabel] = getHandleSliderLabels(placement.face, copy);

  return (
    <div className="mobile-selection-controls" aria-label={copy.editor.mobileSelection.handleAriaLabel}>
      <SegmentedRow
        label={copy.editor.mobileSelection.face}
        options={(["front", "right", "back", "left"] as WallFace[]).map((face) => ({
          label: copy.editor.wallFaceLabels[face],
          active: placement.face === face,
          onClick: () => onMoveHandleFace(placement.id, face)
        }))}
      />
      <RangeField
        endLabel={endLabel}
        label={copy.editor.mobileSelection.horizontalPosition}
        max={designLimits.handleOffset.max}
        min={designLimits.handleOffset.min}
        step={designLimits.handleOffset.step}
        startLabel={startLabel}
        value={placement.offset}
        onChange={(offset) => onMoveHandle(placement.id, offset, placement.verticalOffset)}
      />
      {design.handleStyle === "slot" ? (
        <RangeField
          endLabel={copy.editor.mobileSelection.top}
          label={copy.editor.mobileSelection.height}
          max={designLimits.handleOffset.max}
          min={designLimits.handleOffset.min}
          step={designLimits.handleOffset.step}
          startLabel={copy.editor.mobileSelection.bottom}
          value={placement.verticalOffset ?? 0.58}
          onChange={(verticalOffset) => onMoveHandle(placement.id, placement.offset, verticalOffset)}
        />
      ) : null}
    </div>
  );
}

function RangeField({
  endLabel,
  label,
  max,
  min,
  onChange,
  startLabel,
  step,
  value
}: {
  endLabel: string;
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  startLabel: string;
  step: number;
  value: number;
}) {
  return (
    <label className="range-field">
      <span className="range-field__label">{label}</span>
      <input
        max={max}
        min={min}
        step={step}
        type="range"
        value={value}
        onChange={(event) => onChange(toRatio(Number(event.target.value)))}
      />
      <span className="range-field__ends" aria-hidden="true">
        <span>{startLabel}</span>
        <span>{endLabel}</span>
      </span>
    </label>
  );
}

function ToolButton({
  active,
  copy,
  icon: Icon,
  isMobileEditor,
  label,
  onSelectTool,
  onToolDraggingChange,
  tool
}: {
  active: boolean;
  copy: AppCopy;
  icon: typeof Box;
  isMobileEditor: boolean;
  label: string;
  onSelectTool: (tool: EditorTool | null) => void;
  onToolDraggingChange: (dragging: boolean) => void;
  tool: EditorTool;
}) {
  const longPressTimeoutRef = useRef<number | null>(null);
  const mobileDragStartedRef = useRef(false);
  const mobilePointerCleanupRef = useRef<(() => void) | null>(null);
  const latestPointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const [mobileDragPreviewPosition, setMobileDragPreviewPosition] = useState<{ x: number; y: number } | null>(null);

  const clearLongPressTimeout = () => {
    if (longPressTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(longPressTimeoutRef.current);
    longPressTimeoutRef.current = null;
  };

  const clearMobilePointerTracking = () => {
    if (!mobilePointerCleanupRef.current) {
      return;
    }

    mobilePointerCleanupRef.current();
    mobilePointerCleanupRef.current = null;
  };

  useEffect(() => () => {
    clearLongPressTimeout();
    clearMobilePointerTracking();
  }, []);

  const handleDragStart = (event: DragEvent<HTMLButtonElement>) => {
    onSelectTool(tool);
    onToolDraggingChange(true);
    event.dataTransfer.setData("application/x-hakofit-tool", tool);
    event.dataTransfer.effectAllowed = "copy";
  };

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!isMobileEditor) {
      return;
    }

    event.preventDefault();
    onSelectTool(tool);
    mobileDragStartedRef.current = false;
    latestPointerPositionRef.current = { x: event.clientX, y: event.clientY };
    clearLongPressTimeout();
    clearMobilePointerTracking();

    const handleWindowPointerMove = (pointerEvent: globalThis.PointerEvent) => {
      if (pointerEvent.pointerId !== event.pointerId) {
        return;
      }

      latestPointerPositionRef.current = { x: pointerEvent.clientX, y: pointerEvent.clientY };

      if (mobileDragStartedRef.current) {
        setMobileDragPreviewPosition(latestPointerPositionRef.current);
      }
    };

    const finishMobilePointer = (pointerEvent: globalThis.PointerEvent) => {
      if (pointerEvent.pointerId !== event.pointerId) {
        return;
      }

      clearLongPressTimeout();
      setMobileDragPreviewPosition(null);
      latestPointerPositionRef.current = null;
      mobileDragStartedRef.current = false;
      clearMobilePointerTracking();
    };

    mobilePointerCleanupRef.current = () => {
      window.removeEventListener("pointermove", handleWindowPointerMove);
      window.removeEventListener("pointerup", finishMobilePointer);
      window.removeEventListener("pointercancel", finishMobilePointer);
    };
    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", finishMobilePointer);
    window.addEventListener("pointercancel", finishMobilePointer);

    longPressTimeoutRef.current = window.setTimeout(() => {
      mobileDragStartedRef.current = true;
      onToolDraggingChange(true);
      setMobileDragPreviewPosition(latestPointerPositionRef.current);
    }, 260);
  };

  const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    if (!isMobileEditor) {
      return;
    }

    clearLongPressTimeout();
  };

  const handlePointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
    if (!isMobileEditor) {
      return;
    }

    if (mobileDragStartedRef.current) {
      onSelectTool(null);
      onToolDraggingChange(false);
    }
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isMobileEditor) {
      event.preventDefault();
      onSelectTool(tool);
      return;
    }

    onSelectTool(tool);
  };

  return (
    <>
      <button
      aria-pressed={active}
      aria-label={copy.editor.addToolAriaLabel(label)}
        className={`tool-button ${active ? "is-active" : ""}`}
        draggable={!isMobileEditor}
        type="button"
        onClick={handleClick}
        onContextMenu={(event) => {
          if (isMobileEditor) {
            event.preventDefault();
          }
        }}
        onDragEnd={() => onToolDraggingChange(false)}
        onDragStart={handleDragStart}
        onPointerCancel={handlePointerCancel}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        <Icon size={18} />
        <span>{label}</span>
      </button>
      {mobileDragPreviewPosition ? (
        <div
          aria-hidden="true"
          className="mobile-drag-preview"
          style={{ left: mobileDragPreviewPosition.x, top: mobileDragPreviewPosition.y }}
        >
          <Icon size={18} />
          <span>{label}</span>
        </div>
      ) : null}
    </>
  );
}

function HandleControls({
  copy,
  design,
  onUpdateStyle
}: {
  copy: AppCopy;
  design: StorageDesign;
  onUpdateStyle: (handleStyle: StorageDesign["handleStyle"]) => void;
}) {
  return (
    <SegmentedRow
      label={copy.editor.handleControls.label}
      options={[
        {
          label: copy.editor.handleControls.none,
          active: design.handleStyle === "none",
          onClick: () => onUpdateStyle("none")
        },
        {
          label: copy.editor.handleControls.notch,
          active: design.handleStyle === "notch",
          onClick: () => onUpdateStyle("notch")
        },
        {
          label: copy.editor.handleControls.slot,
          active: design.handleStyle === "slot",
          onClick: () => onUpdateStyle("slot")
        }
      ]}
    />
  );
}

function ModelInteractionLayer({
  copy,
  design,
  activeTool,
  handleFace,
  isMobileEditor,
  isToolDragging,
  onHandleFaceChange,
  onDropTool,
  onSelectTool,
  onSelect
}: {
  copy: AppCopy;
  design: StorageDesign;
  activeTool: EditorTool | null;
  handleFace: WallFace;
  isMobileEditor: boolean;
  isToolDragging: boolean;
  onHandleFaceChange: (face: WallFace) => void;
  onDropTool: (tool: EditorTool, point: PointRatio) => void;
  onSelectTool: (tool: EditorTool | null) => void;
  onSelect: (selection: Selection | null) => void;
}) {
  const planeRef = useRef<HTMLDivElement | null>(null);
  const lastFaceSwitchRef = useRef<{ edge: "left" | "right" | null; at: number }>({ edge: null, at: 0 });
  const handleAxisLength = handleFace === "front" || handleFace === "back" ? design.widthMm : design.depthMm;
  const rawAspect = activeTool === "handle" ? handleAxisLength / Math.max(1, design.heightMm) : design.widthMm / design.depthMm;
  const aspect = Math.max(0.58, Math.min(2.4, rawAspect));

  const getPointFromEvent = (event: Pick<PointerEvent<HTMLElement>, "clientX" | "clientY">): PointRatio => {
    const rect = planeRef.current?.getBoundingClientRect();

    if (!rect) {
      return { x: 0.5, y: 0.5 };
    }

    return {
      x: toRatio((event.clientX - rect.left) / rect.width),
      y: toRatio((event.clientY - rect.top) / rect.height)
    };
  };

  const getRawPointFromEvent = (event: Pick<PointerEvent<HTMLElement>, "clientX" | "clientY">) => {
    const rect = planeRef.current?.getBoundingClientRect();

    if (!rect) {
      return { x: 0.5, y: 0.5 };
    }

    return {
      x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((event.clientY - rect.top) / rect.height, 0, 1)
    };
  };

  const getHandlePointFromSideEvent = (event: Pick<PointerEvent<HTMLElement> | DragEvent<HTMLElement>, "clientX" | "clientY">): PointRatio => {
    const point = getRawPointFromEvent(event);
    const lateralOffset = toRatio(point.x);
    const verticalOffset = toRatio(1 - point.y);

    if (handleFace === "front") {
      return { face: handleFace, vertical: verticalOffset, x: lateralOffset, y: 1 };
    }

    if (handleFace === "back") {
      return { face: handleFace, vertical: verticalOffset, x: lateralOffset, y: 0 };
    }

    return {
      face: handleFace,
      vertical: verticalOffset,
      x: handleFace === "right" ? 1 : 0,
      y: lateralOffset
    };
  };

  const maybeSwitchHandleFace = (event: Pick<PointerEvent<HTMLElement> | DragEvent<HTMLElement>, "clientX" | "clientY">) => {
    if (activeTool !== "handle") {
      return;
    }

    const edge = getHandleFaceSwitchEdge(event, 0.08);

    if (!edge) {
      lastFaceSwitchRef.current.edge = null;
      return;
    }

    const now = Date.now();
    const previous = lastFaceSwitchRef.current;

    if (previous.edge === edge && now - previous.at < 700) {
      return;
    }

    lastFaceSwitchRef.current = { edge, at: now };
    onHandleFaceChange(getAdjacentWallFace(handleFace, edge === "right" ? -1 : 1));
  };

  const getHandleFaceSwitchEdge = (event: Pick<PointerEvent<HTMLElement> | DragEvent<HTMLElement>, "clientX" | "clientY">, edgeRatio: number) => {
    const { x } = getRawPointFromEvent(event);

    if (x < edgeRatio) {
      return "left";
    }

    if (x > 1 - edgeRatio) {
      return "right";
    }

    return null;
  };

  useEffect(() => {
    if (!isMobileEditor || !isToolDragging || !activeTool) {
      return;
    }

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      if (activeTool === "handle") {
        maybeSwitchHandleFace(event);
      }
    };

    const handlePointerUp = (event: globalThis.PointerEvent) => {
      const rect = planeRef.current?.getBoundingClientRect();

      if (!rect) {
        onSelectTool(null);
        return;
      }

      const isInsidePlan = event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!isInsidePlan) {
        onSelectTool(null);
        return;
      }

      onDropTool(activeTool, activeTool === "handle" ? getHandlePointFromSideEvent(event) : getPointFromEvent(event));
      onSelectTool(null);
    };

    const handlePointerCancel = () => {
      onSelectTool(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [activeTool, handleFace, isMobileEditor, isToolDragging, onDropTool, onSelectTool]);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (Array.from(event.dataTransfer.types).includes("application/x-hakofit-tool")) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      maybeSwitchHandleFace(event);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    const tool = event.dataTransfer.getData("application/x-hakofit-tool");

    if (!isEditorTool(tool)) {
      return;
    }

    event.preventDefault();
    if (tool === "handle") {
      onDropTool(tool, getHandlePointFromSideEvent(event));
      onSelectTool(null);
      return;
    }

    const rect = planeRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    const x = toRatio((event.clientX - rect.left) / rect.width);
    const y = toRatio((event.clientY - rect.top) / rect.height);

    onDropTool(tool, { x, y });
    onSelectTool(null);
  };

  const handlePlanePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (activeTool) {
      event.preventDefault();
      event.stopPropagation();
      if (isMobileEditor) {
        return;
      }
      onDropTool(activeTool, activeTool === "handle" ? getHandlePointFromSideEvent(event) : getPointFromEvent(event));
      onSelectTool(null);
      return;
    }

    event.stopPropagation();
    onSelect(null);
  };

  return (
    <div className="model-interaction-layer">
      <div
        className={`model-plan ${activeTool ? "model-plan--active-tool" : ""} ${activeTool === "handle" ? "model-plan--handle-tool" : ""} ${isToolDragging ? "model-plan--dragging-tool" : ""}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onPointerDown={activeTool ? handlePlanePointerDown : undefined}
        onPointerMove={(event) => maybeSwitchHandleFace(event)}
        ref={planeRef}
        style={{ aspectRatio: String(aspect) }}
      >
        <div className="model-plan__box" />
        {activeTool === "handle" ? (
          <div className="handle-side-drop" aria-hidden="true">
            <span className="handle-side-drop__edge handle-side-drop__edge--left">{copy.editor.handleSideDrop.previousFace}</span>
            <span className="handle-side-drop__label">{copy.editor.handleSideDrop.currentFace(copy.editor.wallFaceLabels[handleFace])}</span>
            <span className="handle-side-drop__edge handle-side-drop__edge--right">{copy.editor.handleSideDrop.nextFace}</span>
          </div>
        ) : null}
        {activeTool ? (
          <div className="model-tool-hint">
            {activeTool === "handle" ? (
              <>
                <span className="copy-desktop">{copy.editor.modelHints.handleDesktop(copy.editor.wallFaceLabels[handleFace])}</span>
                <span className="copy-mobile">{copy.editor.modelHints.mobile}</span>
              </>
            ) : (
              <>
                <span className="copy-desktop">{copy.editor.modelHints.dividerDesktop}</span>
                <span className="copy-mobile">{copy.editor.modelHints.mobile}</span>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function isEditorTool(value: string): value is EditorTool {
  return value === "handle" || value === "divider-column" || value === "divider-row";
}

function nearestFace(point: PointRatio): WallFace {
  const candidates: Array<{ face: WallFace; distance: number }> = [
    { face: "left", distance: point.x },
    { face: "right", distance: 1 - point.x },
    { face: "back", distance: point.y },
    { face: "front", distance: 1 - point.y }
  ];

  return candidates.reduce((nearest, candidate) =>
    candidate.distance < nearest.distance ? candidate : nearest
  ).face;
}

function getAdjacentWallFace(face: WallFace, direction: 1 | -1): WallFace {
  const order: WallFace[] = ["front", "right", "back", "left"];
  const currentIndex = order.indexOf(face);
  const nextIndex = (currentIndex + direction + order.length) % order.length;

  return order[nextIndex];
}

function getHandleSliderLabels(face: WallFace, copy: AppCopy): [string, string] {
  return face === "front" || face === "back"
    ? [copy.editor.mobileSelection.left, copy.editor.mobileSelection.right]
    : [copy.editor.mobileSelection.back, copy.editor.mobileSelection.front];
}

function getDividerBreaks(segments: DividerSegment[], axis: DividerAxis): number[] {
  return [0, ...sortedFractions(segments.filter((segment) => segment.axis === axis).map((segment) => segment.fraction)), 1];
}

function canSplitDividerSegment(segments: DividerSegment[], segment: DividerSegment): boolean {
  return !hasOppositeAxisSplitSegment(segments, segment.axis) && hasDividerSplitBreaks(segments, segment);
}

function hasDividerSplitBreaks(segments: DividerSegment[], segment: DividerSegment): boolean {
  const splitAxis = segment.axis === "row" ? "column" : "row";

  return getDividerBreaks(segments, splitAxis)
    .some((fraction) => fraction > segment.startFraction && fraction < segment.endFraction);
}

function hasOppositeAxisSplitSegment(segments: DividerSegment[], axis: DividerAxis): boolean {
  const oppositeAxis = axis === "row" ? "column" : "row";

  return segments.some((segment) => segment.axis === oppositeAxis && isDividerSegmentSplit(segment));
}

function isDividerSegmentSplit(segment: DividerSegment): boolean {
  return !isSameFraction(segment.startFraction, 0) || !isSameFraction(segment.endFraction, 1);
}

function isSameFraction(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.001;
}

function sortedFractions(values: number[]): number[] {
  return values.map(toRatio).sort((a, b) => a - b);
}

async function readDesignFromFiles(files: FileList | null, missingMessage: string): Promise<StorageDesign> {
  const payloads = await readJsonPayloads(files, missingMessage);

  for (const payload of payloads) {
    try {
      return parseStorageDesignJson(payload);
    } catch {
      // Try the next JSON file.
    }
  }

  throw new Error(missingMessage);
}

async function readReexportFiles(
  files: FileList | null,
  missingMessage: string
): Promise<{ design: StorageDesign; entitlement: SignedEntitlement }> {
  const payloads = await readJsonPayloads(files, missingMessage);
  let design: StorageDesign | null = null;
  let entitlement: SignedEntitlement | null = null;

  for (const payload of payloads) {
    if (!design) {
      try {
        design = parseStorageDesignJson(payload);
        continue;
      } catch {
        // Not a design file.
      }
    }

    if (!entitlement) {
      try {
        entitlement = parseSignedEntitlementJson(payload);
      } catch {
        // Not an entitlement file.
      }
    }
  }

  if (!design || !entitlement) {
    throw new Error(missingMessage);
  }

  return { design, entitlement };
}

async function readJsonPayloads(files: FileList | null, missingMessage: string): Promise<unknown[]> {
  const selectedFiles = Array.from(files ?? []);

  if (selectedFiles.length === 0) {
    throw new Error(missingMessage);
  }

  return Promise.all(selectedFiles.map(async (file) => JSON.parse(await file.text()) as unknown));
}

function getNumberFieldLimits(design: StorageDesign): Record<EditableNumberField, NumberFieldLimits> {
  const widthIsValid = isWithinBaseLimit(design.widthMm, designLimits.widthMm);
  const depthIsValid = isWithinBaseLimit(design.depthMm, designLimits.depthMm);
  const heightIsValid = isWithinBaseLimit(design.heightMm, designLimits.heightMm);
  const wallIsValid = isWithinBaseLimit(design.wallMm, designLimits.wallMm);
  const bottomIsValid = isWithinBaseLimit(design.bottomMm, designLimits.bottomMm);
  const wallMax = widthIsValid && depthIsValid
    ? Math.max(designLimits.wallMm.min, Math.min(designLimits.wallMm.max, Math.min(design.widthMm, design.depthMm) / 6))
    : designLimits.wallMm.max;
  const bottomMax = heightIsValid
    ? Math.max(designLimits.bottomMm.min, Math.min(designLimits.bottomMm.max, design.heightMm / 2))
    : designLimits.bottomMm.max;
  const cornerRadiusMax = widthIsValid && depthIsValid && wallIsValid
    ? Math.max(0, Math.min(designLimits.cornerRadiusMm.max, Math.min(design.widthMm, design.depthMm) / 2 - design.wallMm))
    : designLimits.cornerRadiusMm.max;
  const dividerHeightMax = heightIsValid && (!design.hasBottom || bottomIsValid)
    ? Math.max(
      designLimits.dividerHeightMm.min,
      Math.min(designLimits.dividerHeightMm.max, design.heightMm - (design.hasBottom ? design.bottomMm : 0))
    )
    : designLimits.dividerHeightMm.max;

  return {
    widthMm: designLimits.widthMm,
    depthMm: designLimits.depthMm,
    heightMm: designLimits.heightMm,
    wallMm: { ...designLimits.wallMm, max: wallMax },
    bottomMm: { ...designLimits.bottomMm, max: bottomMax },
    cornerRadiusMm: { ...designLimits.cornerRadiusMm, max: cornerRadiusMax },
    handleLengthMm: designLimits.handleLengthMm,
    dividerMm: designLimits.dividerMm,
    dividerHeightMm: { ...designLimits.dividerHeightMm, max: dividerHeightMax }
  };
}

function getNumberFieldErrors(design: StorageDesign, copy: AppCopy): NumberFieldErrors {
  const limits = getNumberFieldLimits(design);
  const errors: NumberFieldErrors = {};
  const fields = Object.keys(limits) as EditableNumberField[];

  for (const field of fields) {
    if (field === "handleLengthMm" && design.handleStyle === "none") {
      continue;
    }

    const value = design[field];
    const limit = limits[field];
    const unit = limit.unit ?? "mm";

    if (!Number.isFinite(value)) {
      errors[field] = copy.editor.validation.required;
      continue;
    }

    if (value < limit.min || value > limit.max) {
      errors[field] = copy.editor.validation.range(formatLimit(limit.min), formatLimit(limit.max), unit);
    }
  }

  return errors;
}

function hasFieldErrors(errors: NumberFieldErrors): boolean {
  return Object.values(errors).some(Boolean);
}

function isWithinBaseLimit(value: number, limit: { min: number; max: number }): boolean {
  return Number.isFinite(value) && value >= limit.min && value <= limit.max;
}

function formatLimit(value: number): string {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1).replace(/\.0$/, "");
}

function reexportValidationMessage(reason: "invalid" | "expired" | "design-type-mismatch" | "design-hash-mismatch", copy: AppCopy): string {
  switch (reason) {
    case "invalid":
      return copy.notices.entitlementInvalid;
    case "expired":
      return copy.notices.entitlementExpired;
    case "design-type-mismatch":
      return copy.notices.entitlementDesignTypeMismatch;
    case "design-hash-mismatch":
      return copy.notices.reexportDesignMismatch;
    default:
      return copy.notices.genericError;
  }
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const updateMatches = () => setMatches(mediaQueryList.matches);

    updateMatches();
    mediaQueryList.addEventListener("change", updateMatches);

    return () => mediaQueryList.removeEventListener("change", updateMatches);
  }, [query]);

  return matches;
}

function getInitialLanguage(): Language {
  const params = new URLSearchParams(window.location.search);
  const queryLanguage = params.get("lang");

  if (isLanguage(queryLanguage)) {
    saveLanguagePreference(queryLanguage);
    return queryLanguage;
  }

  try {
    const savedLanguage = window.localStorage.getItem("hakofit:language");

    if (isLanguage(savedLanguage)) {
      return savedLanguage;
    }
  } catch {
    // localStorage may be unavailable in private contexts.
  }

  return "ja";
}

function isLanguage(value: string | null): value is Language {
  return value === "ja" || value === "en";
}

function saveLanguagePreference(language: Language): void {
  try {
    window.localStorage.setItem("hakofit:language", language);
  } catch {
    // Best effort preference only.
  }
}

function createId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function createSavedDesignName(updatedAt: string, language: Language): string {
  const date = new Date(updatedAt);
  const formatted = formatSavedDesignDate(updatedAt, language);

  if (Number.isNaN(date.getTime())) {
    return language === "ja" ? "保存した設計" : "Saved design";
  }

  return language === "ja" ? `設計 ${formatted}` : `Design ${formatted}`;
}

function formatSavedDesignDate(value: string, language: Language): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function toRatio(value: number): number {
  return Number(clamp(value, designLimits.handleOffset.min, designLimits.handleOffset.max).toFixed(3));
}

function loadSavedDesigns(): SavedDesignRecord[] {
  try {
    const raw = window.localStorage.getItem(savedDesignsStorageKey);
    const parsed = raw ? JSON.parse(raw) : null;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map(parseSavedDesignRecord)
      .filter((record): record is SavedDesignRecord => Boolean(record))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, savedDesignLimit);
  } catch {
    return [];
  }
}

function parseSavedDesignRecord(value: unknown): SavedDesignRecord | null {
  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.id !== "string" || typeof value.name !== "string" || typeof value.updatedAt !== "string") {
    return null;
  }

  try {
    return {
      id: value.id,
      name: value.name,
      updatedAt: value.updatedAt,
      design: parseStorageDesignJson(value.design)
    };
  } catch {
    return null;
  }
}

function persistSavedDesigns(records: SavedDesignRecord[], failureMessage: string): void {
  try {
    window.localStorage.setItem(savedDesignsStorageKey, JSON.stringify(records));
  } catch {
    throw new Error(failureMessage);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function savePendingCheckout(pending: PendingCheckout, failureMessage: string): void {
  try {
    window.localStorage.setItem(pendingCheckoutStorageKey, JSON.stringify(pending));
  } catch {
    throw new Error(failureMessage);
  }
}

function loadPendingCheckout(): PendingCheckout | null {
  try {
    const raw = window.localStorage.getItem(pendingCheckoutStorageKey);

    return raw ? JSON.parse(raw) as PendingCheckout : null;
  } catch {
    return null;
  }
}

function clearPendingCheckout(): void {
  try {
    window.localStorage.removeItem(pendingCheckoutStorageKey);
  } catch {
    // Best effort cleanup only.
  }
}

function clearCheckoutQuery(): void {
  const url = new URL(window.location.href);

  url.searchParams.delete("checkout");
  url.searchParams.delete("session_id");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function getStaticPageSlugFromPath(pathname: string): PageSlug | null {
  switch (pathname.replace(/\/$/, "")) {
    case "/contact":
      return "contact";
    case "/privacy":
      return "privacy";
    case "/terms":
      return "terms";
    case "/commercial-disclosure":
    case "/legal/commercial-disclosure":
      return "commercial-disclosure";
    default:
      return null;
  }
}

function getRuntimeCopy(copy: AppCopy, language: Language, isNativeIos: boolean): AppCopy {
  if (!isNativeIos) {
    return copy;
  }

  const nativeLanding = language === "ja"
    ? {
      ...copy.landing,
      capabilities: ["登録不要", "編集・プレビュー無料", "プリントは外注でOK"],
      pricingTitle: "編集・プレビューは無料。",
      pricingBody: "編集・プレビューまでは無料です。データ出力はアップグレードで解放されます。",
      pricingItems: [
        { term: "編集・プレビュー", description: "無料" },
        { term: "アップグレード", description: "一度購入すると何度でも出力可能" },
        { term: "外部プリント", description: "造形費・送料は各サービスで確認" },
        { term: "配送物", description: "完成品配送ではなくデータ提供" }
      ],
      faqs: copy.landing.faqs.map((faq, index) => index === 0
        ? {
          question: "試すだけでも料金はかかりますか？",
          answer: "いいえ。エディターでサイズや仕切りを調整し、3Dプレビューで形を確認するところまでは無料です。データ出力にはアプリ内アップグレードが必要です。"
        }
        : faq)
    }
    : {
      ...copy.landing,
      capabilities: ["No account required", "Free editing and preview", "One-time export upgrade"],
      pricingTitle: "Editing and preview are free. Export unlocks with an in-app upgrade",
      pricingBody: "In the iOS app, one upgrade unlocks unlimited STL/ZIP exports. Manufacturing and shipping costs still depend on the external print service.",
      pricingItems: [
        { term: "Editing and preview", description: "Free" },
        { term: "Export upgrade", description: "One purchase unlocks unlimited exports" },
        { term: "External printing", description: "Print and shipping costs depend on each service" },
        { term: "Delivery", description: "Data delivery only, not a finished product" }
      ],
      faqs: copy.landing.faqs.map((faq, index) => index === 0
        ? {
          question: "Does it cost anything just to try?",
          answer: "No. Editing dimensions, moving dividers, and checking the 3D preview are free. STL/ZIP export requires the in-app upgrade."
        }
        : faq)
    };

  const nativeCategories = language === "ja"
    ? {
      ...copy.categories,
      backLabel: "このアプリについて"
    }
    : {
      ...copy.categories,
      backLabel: "About this app"
    };

  return {
    ...copy,
    categories: nativeCategories,
    landing: nativeLanding
  };
}

function getRuntimeLegalPage(page: LegalPageContent, language: Language, isNativeIos: boolean): LegalPageContent {
  if (!isNativeIos) {
    return page;
  }

  return {
    ...page,
    sections: page.sections.map((section) => ({
      ...section,
      paragraphs: section.paragraphs?.map((paragraph) => getNativeIosLegalText(paragraph, language)),
      items: section.items?.map((item) => getNativeIosLegalText(item, language)),
      rows: section.rows?.map((row) => getNativeIosLegalRow(page.slug, row, language))
    }))
  };
}

function getNativeIosLegalText(text: string, language: Language): string {
  if (language === "ja") {
    switch (text) {
      case "決済関連情報：クレジットカード情報は決済代行事業者であるStripe等が管理し、事業者はカード番号全体を保持・参照しません。事業者はCheckout Session ID、決済金額、決済日時、決済ステータス等を取得することがあります。":
        return "アプリ内課金関連情報：iOS版の出力アップグレードはAppleのIn-App Purchaseを通じて処理され、事業者はクレジットカード番号等を保持・参照しません。事業者は購入状態、商品ID、Transaction ID、購入日時、返金・取消状態等を確認することがあります。";
      case "本サービスの提供、運営、決済確認、3Dプリント用データの出力、再出力キーの発行のため":
        return "本サービスの提供、運営、購入状態の確認、3Dプリント用データの出力、設計データの復元のため";
      case "本サービスでは、決済前の設計データの一時保存、決済後の復元、利便性向上、アクセス解析等のためにCookie、localStorageその他の技術を利用することがあります。":
        return "本サービスでは、設計データの一時保存、出力後の復元、購入状態に応じた表示、利便性向上、アクセス解析等のためにlocalStorageその他の技術を利用することがあります。";
      case "ユーザーはブラウザの設定によりCookie等を制限できますが、その場合、決済後のダウンロードや一部機能が正常に動作しないことがあります。":
        return "ユーザーの端末設定やアプリ環境によっては、設計データの復元、出力、購入状態の確認など一部機能が正常に動作しないことがあります。";
      case "クレジットカード決済等を提供する決済代行事業者":
        return "App Storeのアプリ内課金および購入管理を提供するApple";
      case "本サービスの有料出力には、データ作成料が発生します。現在のデータ作成料は税込550円です。ただし、価格を変更する場合は、本サイト上または決済画面に表示する金額を優先します。":
        return "iOS版のSTL/ZIP出力には、アプリ内の出力アップグレード購入が必要です。価格はApp Storeの購入画面に表示される金額を優先します。";
      case "データ作成料は、STL生成および設計データ作成の対価であり、外部プリントサービスの造形費、送料、税金、素材費、その他費用を含みません。":
        return "出力アップグレードは、STL生成および設計データ作成機能の解放の対価であり、外部プリントサービスの造形費、送料、税金、素材費、その他費用を含みません。";
      case "ユーザーは、データ作成料を事業者が指定する決済手段（クレジットカード決済等）により支払うものとします。":
        return "ユーザーは、iOS版の出力アップグレードをApp Storeのアプリ内課金により購入するものとします。";
      case "決済処理はStripe等の決済代行事業者を通じて行われます。":
        return "iOS版の決済処理はAppleのIn-App Purchaseを通じて行われます。";
      default:
        return text;
    }
  }

  switch (text) {
    case "Payment-related information: full credit card numbers are managed by payment processors such as Stripe, and the operator does not store or view the full card number. The operator may collect Checkout Session IDs, payment amounts, payment dates and times, payment status, and similar information.":
      return "In-app purchase information: the iOS export upgrade is processed through Apple's In-App Purchase, and the operator does not store or view full credit card numbers. The operator may check purchase status, product IDs, transaction IDs, purchase dates, refund or revocation status, and similar information.";
    case "To provide and operate this service, confirm payments, export 3D print data, and issue re-export keys.":
      return "To provide and operate this service, check purchase status, export 3D print data, and restore design data.";
    case "This service may use cookies, localStorage, and similar technologies to temporarily save design data before payment, restore it after payment, improve convenience, and analyze access.":
      return "This service may use localStorage and similar technologies to temporarily save design data, restore it after export, adjust the display based on purchase status, improve convenience, and analyze access.";
    case "Users can restrict cookies and similar technologies through browser settings, but in that case downloads after payment or some functions may not work properly.":
      return "Depending on device settings and the app environment, restoring design data, exporting, checking purchase status, or some functions may not work properly.";
    case "Payment processors that provide credit card payments and similar services.":
      return "Apple, which provides App Store in-app purchase and purchase management.";
    case "Paid export from this service requires a data creation fee. The current data creation fee is 550 yen including tax. If the price changes, the amount shown on this website or the checkout screen takes precedence.":
      return "STL/ZIP export in the iOS app requires the in-app export upgrade. The price shown on the App Store purchase sheet takes precedence.";
    case "The data creation fee is consideration for 3D print data generation and design data creation, and does not include manufacturing fees, shipping fees, taxes, material costs, or other costs charged by external print services.":
      return "The export upgrade unlocks STL generation and design data creation features, and does not include manufacturing fees, shipping fees, taxes, material costs, or other costs charged by external print services.";
    case "Users shall pay the data creation fee by the payment method designated by the operator, such as credit card payment.":
      return "Users shall purchase the iOS export upgrade through App Store in-app purchase.";
    case "Payment processing is handled through payment processors such as Stripe.":
      return "Payment processing in the iOS app is handled through Apple's In-App Purchase.";
    default:
      return text;
  }
}

function getNativeIosLegalRow(
  slug: PageSlug,
  row: { label: string; value: string },
  language: Language
): { label: string; value: string } {
  if (slug !== "commercial-disclosure") {
    return row;
  }

  if (language === "ja") {
    switch (row.label) {
      case "販売価格":
        return { ...row, value: "iOS版出力アップグレード：App Storeの購入画面に表示される金額" };
      case "支払方法":
        return { ...row, value: "App Storeのアプリ内課金" };
      case "引渡時期":
        return { ...row, value: "出力アップグレードの購入完了後、アプリ内でSTL/ZIP出力が利用可能になります。" };
      default:
        return row;
    }
  }

  switch (row.label) {
    case "Selling price":
      return { ...row, value: "iOS export upgrade: the amount shown on the App Store purchase sheet." };
    case "Payment method":
      return { ...row, value: "App Store in-app purchase." };
    case "Delivery timing":
      return { ...row, value: "After the export upgrade purchase is complete, STL/ZIP export is available in the app." };
    default:
      return row;
  }
}

function getSeoMetadata(slug: PageSlug | null, language: Language): SeoMetadata {
  const copy = copyByLanguage[language];

  if (!slug) {
    return {
      title: copy.seo.defaultTitle,
      description: copy.seo.defaultDescription,
      path: "/"
    };
  }

  if (slug === "contact") {
    return {
      title: copy.seo.contactTitle,
      description: copy.seo.contactDescription,
      path: pagePathMap.contact
    };
  }

  const legalPage = localizedLegalPages[language][slug];
  const description = slug === "commercial-disclosure"
    ? copy.seo.commercialDisclosureDescription
    : legalPage.lead ?? (slug === "privacy" ? copy.seo.privacyFallbackDescription : copy.seo.termsFallbackDescription);

  return {
    title: `${legalPage.title} | ${copy.brand.name}`,
    description,
    path: pagePathMap[slug]
  };
}

function applyDocumentSeo(metadata: SeoMetadata, language: Language): void {
  const copy = copyByLanguage[language];
  const canonicalUrl = new URL(metadata.path, siteOrigin).toString();

  document.documentElement.lang = language;
  document.title = metadata.title;
  upsertMeta("name", "description", metadata.description);
  upsertMeta("property", "og:site_name", copy.brand.name);
  upsertMeta("property", "og:locale", language === "ja" ? "ja_JP" : "en_US");
  upsertMeta("property", "og:title", metadata.title);
  upsertMeta("property", "og:description", metadata.description);
  upsertMeta("property", "og:url", canonicalUrl);
  upsertMeta("property", "og:image:alt", copy.seo.ogImageAlt);
  upsertMeta("name", "twitter:title", metadata.title);
  upsertMeta("name", "twitter:description", metadata.description);
  upsertCanonical(canonicalUrl);
}

function upsertMeta(attribute: "name" | "property", key: string, content: string): void {
  let element = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
}

function upsertCanonical(url: string): void {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = url;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export default App;
